/**
 * Persisted display preferences for the mobile Orders screen — group by, sort,
 * and which row-detail fields show. Scoped per store in localStorage so the
 * choice survives reloads, mirroring the design's Display sheet.
 */

import { useCallback, useEffect, useState } from "react";
import type { OrdersGroupBy, OrdersSort, RowProp } from "./orders-mobile-model";

export type RowProps = Record<RowProp, boolean>;

export type OrdersDisplayPrefs = {
  group: OrdersGroupBy;
  sort: OrdersSort;
  props: RowProps;
};

const DEFAULTS: OrdersDisplayPrefs = {
  group: "status",
  sort: "newest",
  props: {
    customer: true,
    items: true,
    value: true,
    time: true,
    location: false,
    payment: false,
    channel: false,
  },
};

const VERSION = 1;
const key = (storeSlug: string) => `seller:orders-display:v${VERSION}:${storeSlug}`;

function read(storeSlug: string): OrdersDisplayPrefs {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(key(storeSlug));
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<OrdersDisplayPrefs>;
    return {
      group: parsed.group ?? DEFAULTS.group,
      sort: parsed.sort ?? DEFAULTS.sort,
      props: { ...DEFAULTS.props, ...(parsed.props ?? {}) },
    };
  } catch {
    return DEFAULTS;
  }
}

export function useOrdersDisplayPrefs(storeSlug: string) {
  const [prefs, setPrefs] = useState<OrdersDisplayPrefs>(() => read(storeSlug));

  useEffect(() => {
    setPrefs(read(storeSlug));
  }, [storeSlug]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key(storeSlug), JSON.stringify(prefs));
    } catch {
      /* ignore */
    }
  }, [prefs, storeSlug]);

  const setGroup = useCallback((group: OrdersGroupBy) => setPrefs((p) => ({ ...p, group })), []);
  const setSort = useCallback((sort: OrdersSort) => setPrefs((p) => ({ ...p, sort })), []);
  const toggleProp = useCallback(
    (prop: RowProp) => setPrefs((p) => ({ ...p, props: { ...p.props, [prop]: !p.props[prop] } })),
    [],
  );
  const resetProps = useCallback(() => setPrefs((p) => ({ ...p, props: { ...DEFAULTS.props } })), []);

  return { prefs, setGroup, setSort, toggleProp, resetProps };
}
